import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Avatar,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AgentActionsDropdown from './AgentActionsDropdown';
import { Agent } from '../../types/business';

interface AgentsTableProps {
  agents: Agent[];
  isLoading: boolean;
  error: string | null;
  totalAgents: number;
  isAddingAgent: boolean;
  onAddAgent: () => void;
  onEditAgent: (agent: Agent) => void;
}

const AgentsTable: React.FC<AgentsTableProps> = ({
  agents,
  isLoading,
  error,
  totalAgents,
  isAddingAgent,
  onAddAgent,
  onEditAgent
}) => {
  return (
    <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        borderBottom: 1, 
        borderColor: 'divider' 
      }}>
        <Typography variant="h6">
          Agents ({isLoading ? '...' : totalAgents})
        </Typography>
        <Button 
          startIcon={<AddIcon />} 
          variant="contained" 
          size="small"
          onClick={onAddAgent}
          disabled={isAddingAgent || isLoading}
        >
          Add Agent
        </Button>
      </Box>

      {error && (
        <Box sx={{ p: 2, bgcolor: 'error.light' }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      
      {/* Loading State */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            Loading agents...
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell align="center">Total Homes</TableCell>
                <TableCell align="center">Homes (30d)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agents.map((agent) => (
                <TableRow key={agent.user.email} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2, bgcolor: agent.isActive ? 'primary.main' : 'grey.400' }}>
                        {agent.name.charAt(0)}
                      </Avatar>
                      {agent.name}
                    </Box>
                  </TableCell>
                  <TableCell>{agent.email}</TableCell>
                  <TableCell>{agent.phone || 'N/A'}</TableCell>
                  <TableCell align="center">{agent.stats?.totalHomes || 0}</TableCell>
                  <TableCell align="center">{agent.stats?.homesThisMonth || 0}</TableCell>
                  <TableCell>
                    <Chip 
                      label={agent.isActive ? 'active' : 'inactive'} 
                      color={agent.isActive ? 'success' : 'default'}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <AgentActionsDropdown agent={agent} onEdit={onEditAgent} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default AgentsTable;